import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  limit,
  or,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './useAuth';

export const useConnections = () => {
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);         // accepted connections
  const [pendingConnections, setPendingConnections] = useState([]); // incoming requests
  const [sentRequests, setSentRequests] = useState([]);       // requests I sent (pending)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) return;

    // All connections involving this user
    const q = query(
      collection(db, 'connections'),
      where('fromUserId', '==', user.uid)
    );
    const q2 = query(
      collection(db, 'connections'),
      where('toUserId', '==', user.uid)
    );

    const unsubFrom = onSnapshot(q, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setConnections(prev => {
        const toSide = prev.filter(c => c.toUserId === user.uid && c.status === 'accepted');
        const fromAccepted = docs.filter(c => c.status === 'accepted');
        const fromPending = docs.filter(c => c.status === 'pending');
        setSentRequests(fromPending);
        return [...new Map([...toSide, ...fromAccepted].map(c => [c.id, c])).values()];
      });
      setLoading(false);
    }, (err) => { setError(err.message); setLoading(false); });

    const unsubTo = onSnapshot(q2, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const incoming = docs.filter(c => c.status === 'pending');
      const accepted = docs.filter(c => c.status === 'accepted');
      setPendingConnections(incoming);
      setConnections(prev => {
        const fromSide = prev.filter(c => c.fromUserId === user.uid && c.status === 'accepted');
        return [...new Map([...fromSide, ...accepted].map(c => [c.id, c])).values()];
      });
    });

    return () => { unsubFrom(); unsubTo(); };
  }, [user?.uid]);

  // Send a connection request to another user by their userId
  const sendConnectionRequest = async (toUserId, toUserName, toUserEmail) => {
    if (!user?.uid) throw new Error('Must be logged in');

    // Check not already connected or pending
    const alreadyExists = [...connections, ...pendingConnections, ...sentRequests].some(
      c => (c.fromUserId === toUserId || c.toUserId === toUserId)
    );
    if (alreadyExists) throw new Error('Connection already exists or pending');

    try {
      const connRef = await addDoc(collection(db, 'connections'), {
        fromUserId: user.uid,
        fromUserName: user.displayName || user.email,
        fromUserEmail: user.email,
        toUserId,
        toUserName,
        toUserEmail,
        status: 'pending',
        createdAt: new Date().toISOString(),
      });

      // Notify recipient
      await addDoc(collection(db, 'notifications'), {
        userId: toUserId,
        type: 'connection_request',
        payload: {
          connectionId: connRef.id,
          fromUserId: user.uid,
          fromUserName: user.displayName || user.email,
          fromUserEmail: user.email,
        },
        read: false,
        createdAt: new Date().toISOString(),
      });

      return connRef.id;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Accept or decline a connection request
  const respondToConnection = async (connectionId, accept) => {
    if (!user?.uid) return;
    try {
      await updateDoc(doc(db, 'connections', connectionId), {
        status: accept ? 'accepted' : 'declined',
        respondedAt: new Date().toISOString(),
      });

      if (accept) {
        const conn = pendingConnections.find(c => c.id === connectionId);
        if (conn) {
          await addDoc(collection(db, 'notifications'), {
            userId: conn.fromUserId,
            type: 'connection_accepted',
            payload: {
              connectionId,
              byUserId: user.uid,
              byUserName: user.displayName || user.email,
            },
            read: false,
            createdAt: new Date().toISOString(),
          });
        }
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Remove / cancel a connection
  const removeConnection = async (connectionId) => {
    if (!user?.uid) return;
    try {
      await deleteDoc(doc(db, 'connections', connectionId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Check connection status with a specific user
  const getConnectionStatus = (otherUserId) => {
    if (connections.some(c => c.fromUserId === otherUserId || c.toUserId === otherUserId)) {
      return 'connected';
    }
    if (sentRequests.some(c => c.toUserId === otherUserId)) return 'pending_sent';
    if (pendingConnections.some(c => c.fromUserId === otherUserId)) return 'pending_received';
    return 'none';
  };

  return {
    connections,
    pendingConnections,
    sentRequests,
    loading,
    error,
    sendConnectionRequest,
    respondToConnection,
    removeConnection,
    getConnectionStatus,
  };
};

export default useConnections;
